import React, { useState } from 'react';
import { ThemedButton } from '../ThemedButton';
import { CheckCircle2, Loader2, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useWizard } from '../../contexts/WizardContext';
import { useNavigate } from 'react-router-dom';
import { getAttribution } from '../../utils/attribution';
import { trackGenerateLead } from '../../utils/analytics';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ReviewSubmit = () => {
  const { formData, clearDraft } = useWizard();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [attribution] = useState(() => getAttribution());

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        utm_source: attribution?.utm_source ?? null,
        utm_medium: attribution?.utm_medium ?? null,
        utm_campaign: attribution?.utm_campaign ?? null,
      };
      await axios.post(`${API_URL}/applications`, payload);
      trackGenerateLead();
      clearDraft();
      setSubmitted(true);
      toast.success('Application submitted successfully!');
      setTimeout(() => {
        navigate('/');
      }, 4000);
    } catch (error) {
      toast.error('Failed to submit application. Please try again.');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="relative inline-block mb-6">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-200 to-cyan-200 rounded-full blur-xl opacity-50"></div>
          <CheckCircle2 className="relative h-16 w-16 text-purple-500 mx-auto" />
        </div>
        <h3 className="text-2xl font-bold text-slate-900 mb-4">Application Submitted!</h3>
        <p className="text-slate-600 mb-6">
          Thank you for your application. A Players Health employee will follow up with a quote within 12-24 hours.
        </p>
        <p className="text-sm text-slate-500">
          You'll receive an email at <strong>{formData.applicant_email}</strong> with your quote details.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Hidden lead-source attribution fields (auto-populated before submit) */}
      <input type="hidden" name="utm_source" data-testid="utm-source-hidden" value={attribution?.utm_source ?? 'direct'} readOnly />
      <input type="hidden" name="utm_medium" data-testid="utm-medium-hidden" value={attribution?.utm_medium ?? 'direct'} readOnly />
      <input type="hidden" name="utm_campaign" data-testid="utm-campaign-hidden" value={attribution?.utm_campaign ?? 'direct'} readOnly />
      <Alert className="border-amber-200 bg-amber-50">
        <AlertCircle className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          <strong>Important:</strong> This submission does NOT bind coverage and no payment is collected. This is an underwriting intake only.
        </AlertDescription>
      </Alert>

      <p className="text-slate-600">
        Please review your information before submitting your application.
      </p>

      <div className="space-y-4">
        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Contact Information</h3>
          <dl className="grid grid-cols-1 gap-2 text-sm">
            <div>
              <dt className="text-slate-600">Name:</dt>
              <dd className="text-slate-900 font-medium">{formData.first_name} {formData.last_name}</dd>
            </div>
            <div>
              <dt className="text-slate-600">Email:</dt>
              <dd className="text-slate-900 font-medium">{formData.applicant_email}</dd>
            </div>
            <div>
              <dt className="text-slate-600">Phone:</dt>
              <dd className="text-slate-900 font-medium">{formData.applicant_phone}</dd>
            </div>
            {formData.date_of_birth && (
              <div>
                <dt className="text-slate-600">Date of Birth:</dt>
                <dd className="text-slate-900 font-medium">{formData.date_of_birth} {formData.age && `(Age: ${formData.age})`}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Organization Details</h3>
          <dl className="grid grid-cols-1 gap-2 text-sm">
            <div>
              <dt className="text-slate-600">Organization:</dt>
              <dd className="text-slate-900 font-medium">{formData.org_name}</dd>
            </div>
            {formData.org_type && (
              <div>
                <dt className="text-slate-600">Organization Type:</dt>
                <dd className="text-slate-900 font-medium">{formData.org_type}</dd>
              </div>
            )}
            {formData.website && (
              <div>
                <dt className="text-slate-600">Website:</dt>
                <dd className="text-slate-900 font-medium">{formData.website}</dd>
              </div>
            )}
            <div>
              <dt className="text-slate-600">Address:</dt>
              <dd className="text-slate-900 font-medium">
                {formData.street_address}{formData.unit && `, ${formData.unit}`}<br />
                {formData.org_city}, {formData.org_state} {formData.postal_code}
              </dd>
            </div>
          </dl>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Safety Policies</h3>
          <dl className="grid grid-cols-1 gap-2 text-sm">
            {[
              { key: 'has_waiver', label: 'Participant Waiver' },
              { key: 'has_code_of_conduct', label: 'Code of Conduct' },
              { key: 'has_abuse_prevention_policy', label: 'Abuse Prevention Policy' },
              { key: 'has_background_checks', label: 'Background Checks' },
              { key: 'has_one_on_one_policy', label: '1-on-1 Interaction Policy' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center justify-between">
                <dt className="text-slate-600">{label}:</dt>
                <dd className={`font-medium ${formData[key] ? 'text-green-700' : formData[key] === false ? 'text-red-600' : 'text-slate-400'}`}>
                  {formData[key] === true ? 'Yes' : formData[key] === false ? 'No' : '—'}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Sports & Coverage Details</h3>
          <dl className="grid grid-cols-1 gap-2 text-sm">
            {formData.sports && formData.sports.length > 0 && (
              <div>
                <dt className="text-slate-600 mb-2">Sports Offered:</dt>
                <dd className="text-slate-900">
                  <div className="flex flex-wrap gap-2">
                    {formData.sports.map((s, idx) => (
                      <span key={idx} className="inline-flex items-center bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded-full">
                        {s.sport}: {s.participants} participants
                      </span>
                    ))}
                  </div>
                </dd>
              </div>
            )}
            <div>
              <dt className="text-slate-600">Total Participants Under 19:</dt>
              <dd className="text-slate-900 font-medium">{formData.num_participants_under_19?.toLocaleString()}</dd>
            </div>
            {/* Conditional Coverage Dates Display */}
            {formData.coverage_type === 'event' && formData.event_start_date && formData.event_end_date && (
              <div>
                <dt className="text-slate-600">Event Coverage Period:</dt>
                <dd className="text-slate-900 font-medium">
                  {new Date(formData.event_start_date).toLocaleDateString()} - {new Date(formData.event_end_date).toLocaleDateString()}
                </dd>
              </div>
            )}
            {formData.coverage_type === 'camp' && formData.camp_start_date && formData.camp_end_date && (
              <div>
                <dt className="text-slate-600">Camp/Clinic Coverage Period:</dt>
                <dd className="text-slate-900 font-medium">
                  {new Date(formData.camp_start_date).toLocaleDateString()} - {new Date(formData.camp_end_date).toLocaleDateString()}
                </dd>
              </div>
            )}
            {formData.coverage_type === 'annual' && formData.policy_effective_date && (
              <div>
                <dt className="text-slate-600">Policy Effective Date:</dt>
                <dd className="text-slate-900 font-medium">
                  {new Date(formData.policy_effective_date).toLocaleDateString()} (12-month term)
                </dd>
              </div>
            )}
            {/* Fallback for legacy data */}
            {!formData.coverage_type && formData.coverage_start_date && (
              <div>
                <dt className="text-slate-600">Effective Date:</dt>
                <dd className="text-slate-900 font-medium">{formData.coverage_start_date}</dd>
              </div>
            )}
            <div>
              <dt className="text-slate-600">General Liability:</dt>
              <dd className="text-slate-900 font-medium">
                {formData.gl_limit_per_occurrence} per occurrence / {formData.gl_limit_aggregate} aggregate
              </dd>
            </div>
            {formData.requested_optional_coverages?.length > 0 && (
              <div>
                <dt className="text-slate-600">Optional Coverage Interest:</dt>
                <dd className="text-slate-900 font-medium">
                  {formData.requested_optional_coverages.join(', ')}
                </dd>
              </div>
            )}
            <div>
              <dt className="text-slate-600">Participant Accident:</dt>
              <dd className="text-slate-900 font-medium">{formData.participant_accident_limit}</dd>
            </div>
          </dl>
        </div>

        <div className="border border-slate-200 rounded-lg p-4">
          <h3 className="font-semibold text-slate-900 mb-3">Additional Information</h3>
          <dl className="grid grid-cols-1 gap-2 text-sm">
            <div>
              <dt className="text-slate-600">Claims History:</dt>
              <dd className="text-slate-900 font-medium">
                {formData.claims_history_flag ? 'Yes' : 'No'}
              </dd>
            </div>
            {formData.claims_history_flag && formData.claims_notes && (
              <div>
                <dt className="text-slate-600">Claims Details:</dt>
                <dd className="text-slate-900 text-xs">{formData.claims_notes.substring(0, 100)}...</dd>
              </div>
            )}
            <div>
              <dt className="text-slate-600">Additional Insured:</dt>
              <dd className="text-slate-900 font-medium">
                {formData.additional_insured_required ? 'Yes' : 'No'}
              </dd>
            </div>
            <div>
              <dt className="text-slate-600">Fraud Warranty:</dt>
              <dd className="text-slate-900 font-medium">
                {formData.fraud_warranty_accepted ? 'Accepted' : 'Not Accepted'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
        <h4 className="font-semibold text-slate-900 mb-2">What Happens Next?</h4>
        <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
          <li>A Players Health employee will review your application</li>
          <li>You'll receive a customized quote within 12-24 hours</li>
          <li>No coverage is bound until you accept the quote</li>
          <li>No payment is required at this time</li>
        </ul>
      </div>

      <ThemedButton 
        onClick={handleSubmit}
        data-testid="submit-application-btn"
        disabled={submitting}
        variant="wizard" size="full"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Submitting Application...
          </>
        ) : (
          'Submit Application'
        )}
      </ThemedButton>
    </div>
  );
};

export default ReviewSubmit;
